import React, { useState } from 'react';
import { 
  TrendingUp, DollarSign, CheckCircle, CheckCircle2, HelpCircle, Info, BookOpen
} from 'lucide-react';
import { MODALITY_ICONS } from './constants';

export const ModalityCard = ({ modality, selected, onClick }) => {
  const [showEffectivenessInfo, setShowEffectivenessInfo] = useState(false);
  const [showCostInfo, setShowCostInfo] = useState(false);
  
  const IconComponent = MODALITY_ICONS[modality.id] || BookOpen;
  
  // Value score calculation
  const valueScore = (modality.effectiveness / modality.cost_multiplier).toFixed(2);
  const isHighValue = parseFloat(valueScore) > 0.8;
  const isBestValue = parseFloat(valueScore) > 1.0;
  
  // Effectiveness research data
  const getEffectivenessResearch = () => {
    const effectivenessPercent = (modality.effectiveness * 100).toFixed(0);
    
    const researchData = {
      on_the_job: {
        basis: "70-20-10 Model (Lombardo & Eichinger)",
        explanation: "On-the-job learning accounts for 70% of professional development effectiveness. Direct application during real work creates immediate transfer and retention.",
        studies: [
          "Center for Creative Leadership: 70-20-10 research",
          "Journal of Applied Psychology: 85% retention with immediate application",
          "McKinsey: 2.5x ROI for embedded learning programs"
        ],
        effectiveness_drivers: ["Immediate application", "Real context", "Direct feedback loop", "No transfer gap"]
      },
      simulation: {
        basis: "Experiential Learning Theory (Kolb)",
        explanation: "Simulation provides risk-free practice with 88% effectiveness. Learners can make mistakes safely while building muscle memory and decision patterns.",
        studies: [
          "Aviation industry: 90%+ retention in flight simulators",
          "Medical training: 86% skill transfer to real procedures",
          "Harvard Business Review: VR training 4x faster than classroom"
        ],
        effectiveness_drivers: ["Safe practice environment", "Immediate feedback", "Repeatable scenarios", "Emotional engagement"]
      },
      in_person_ilt: {
        basis: "Social Learning Theory (Bandura)",
        explanation: "In-person instruction enables real-time Q&A, peer learning, and instructor adaptation. The 85% effectiveness reflects strong engagement and social dynamics.",
        studies: [
          "ATD Research: 85% satisfaction for ILT vs 70% for e-learning",
          "Brandon Hall: 80% knowledge retention with interactive facilitation",
          "Kirkpatrick evaluation data across 1000+ programs"
        ],
        effectiveness_drivers: ["Live interaction", "Real-time adaptation", "Social learning", "Immediate clarification"]
      },
      blended: {
        basis: "Spaced Learning + Multi-modal Research",
        explanation: "Blended learning combines the flexibility of digital with the depth of in-person, achieving 80% effectiveness through spaced repetition and varied modalities.",
        studies: [
          "US Dept of Education: Blended outperforms pure online by 35%",
          "Brandon Hall: 30% reduction in training time",
          "Industry average from 200+ blended program evaluations"
        ],
        effectiveness_drivers: ["Spaced repetition", "Multi-modal engagement", "Flexibility with depth", "Pre/post reinforcement"]
      },
      peer_learning: {
        basis: "Social Constructivism (Vygotsky)",
        explanation: "Peer learning leverages Zone of Proximal Development, achieving 78% effectiveness through collaborative knowledge construction and tacit knowledge transfer.",
        studies: [
          "LinkedIn Workplace Learning Report: 75% prefer peer learning",
          "Deloitte: Social learning 4x more engaging",
          "Research on communities of practice effectiveness"
        ],
        effectiveness_drivers: ["Tacit knowledge transfer", "Social accountability", "Contextual relevance", "Network building"]
      },
      virtual_ilt: {
        basis: "Distance Learning Research",
        explanation: "Virtual ILT maintains 75% effectiveness through live instruction while eliminating travel. Effectiveness varies with platform quality and facilitator skill.",
        studies: [
          "Post-COVID meta-analysis: 75-80% parity with in-person",
          "Training Industry: Best practices achieve 85% of ILT results",
          "Engagement drops 15-20% vs in-person (attention research)"
        ],
        effectiveness_drivers: ["Live instruction", "Interactive features", "Recording availability", "Geographic reach"]
      },
      microlearning: {
        basis: "Ebbinghaus Forgetting Curve + Cognitive Load Theory",
        explanation: "Microlearning's 65% effectiveness comes from reduced cognitive load and spaced reinforcement. Best for knowledge maintenance, not initial skill building.",
        studies: [
          "Journal of Applied Psychology: 17% better retention with spacing",
          "Bersin research: 50% more engaging than traditional",
          "Mobile learning completion rates 2x higher"
        ],
        effectiveness_drivers: ["Reduced cognitive load", "Spaced repetition", "Just-in-time access", "Mobile accessibility"]
      },
      self_paced_elearning: {
        basis: "ADDIE Model Evaluations",
        explanation: "Self-paced e-learning achieves 55% effectiveness due to low completion rates (typically 20-30%) and limited interaction. Cost-effective for large scale, basic content.",
        studies: [
          "Industry average completion: 20-30%",
          "Effectiveness gap vs ILT: 30-40% (Brandon Hall)",
          "Best for compliance: 70% effectiveness for knowledge transfer"
        ],
        effectiveness_drivers: ["Scalability", "Consistency", "Self-direction", "Anytime access"]
      }
    };
    
    return researchData[modality.id] || {
      basis: "Industry Benchmark Research",
      explanation: `This ${effectivenessPercent}% effectiveness score is based on meta-analysis of learning outcome studies across modalities.`,
      studies: ["ATD State of the Industry", "Brandon Hall Research", "Training Industry benchmarks"],
      effectiveness_drivers: ["Research-validated methodology"]
    };
  };
  
  // Cost multiplier research data
  const getCostResearch = () => {
    const costData = {
      on_the_job: {
        multiplier: 1.2,
        explanation: "1.2x multiplier reflects dedicated coach/mentor time and productivity impact during learning. Higher than classroom due to 1:1 or small group attention.",
        components: [
          { name: "Coach/mentor time", percent: "50%", note: "Dedicated expert resource" },
          { name: "Productivity impact", percent: "30%", note: "Learner working slower" },
          { name: "Materials/tools", percent: "10%", note: "Job aids, checklists" },
          { name: "Administration", percent: "10%", note: "Scheduling, tracking" }
        ],
        roi_note: "Despite higher cost, delivers 2-3x ROI due to immediate application"
      },
      simulation: {
        multiplier: 1.5,
        explanation: "1.5x multiplier reflects technology investment (VR/simulation platforms) and scenario development costs. Highest upfront cost but scales well.",
        components: [
          { name: "Technology/platform", percent: "40%", note: "VR hardware, simulation software" },
          { name: "Content development", percent: "35%", note: "Scenario design, programming" },
          { name: "Facilitator/tech support", percent: "15%", note: "Session management" },
          { name: "Maintenance", percent: "10%", note: "Updates, equipment" }
        ],
        roi_note: "Cost per learner decreases significantly at scale (50+ learners)"
      },
      in_person_ilt: {
        multiplier: 1.0,
        explanation: "1.0x is the baseline multiplier. Traditional classroom training includes instructor fees, venue, materials, and participant travel/time.",
        components: [
          { name: "Instructor fees", percent: "35%", note: "Facilitator compensation" },
          { name: "Venue/facilities", percent: "25%", note: "Room rental, equipment" },
          { name: "Materials", percent: "15%", note: "Workbooks, handouts" },
          { name: "Travel/logistics", percent: "25%", note: "Participant travel, meals" }
        ],
        roi_note: "Benchmark for comparing all other modalities"
      },
      blended: {
        multiplier: 0.8,
        explanation: "0.8x multiplier achieved by moving some content online, reducing seat time and travel while maintaining in-person components for critical skills.",
        components: [
          { name: "Online content", percent: "30%", note: "E-learning development" },
          { name: "Reduced ILT", percent: "40%", note: "Shorter in-person sessions" },
          { name: "Platform costs", percent: "20%", note: "LMS, virtual tools" },
          { name: "Administration", percent: "10%", note: "Scheduling both modalities" }
        ],
        roi_note: "Sweet spot: 80% effectiveness at 80% cost = best value ratio"
      },
      peer_learning: {
        multiplier: 0.6,
        explanation: "0.6x multiplier reflects minimal external costs - leverages internal expertise. Main costs are program design, facilitation, and participant time.",
        components: [
          { name: "Program design", percent: "25%", note: "Structure, guidelines" },
          { name: "Internal facilitator", percent: "30%", note: "Coordinator time" },
          { name: "Participant time", percent: "35%", note: "Learning activities" },
          { name: "Platform/tools", percent: "10%", note: "Collaboration tools" }
        ],
        roi_note: "Hidden cost: expert employee time; hidden benefit: knowledge retention"
      },
      virtual_ilt: {
        multiplier: 0.5,
        explanation: "0.5x multiplier eliminates venue and travel costs. Instructor fees remain, but platform costs are lower than physical facilities.",
        components: [
          { name: "Instructor fees", percent: "45%", note: "Same as ILT" },
          { name: "Platform/technology", percent: "25%", note: "Zoom, Teams, etc." },
          { name: "Digital materials", percent: "15%", note: "Shared resources" },
          { name: "Administration", percent: "15%", note: "Tech support, scheduling" }
        ],
        roi_note: "75% effectiveness at 50% cost = strong value for remote teams"
      },
      microlearning: {
        multiplier: 0.4,
        explanation: "0.4x multiplier reflects low per-module development cost and high scalability. Content is bite-sized, reducing production and consumption time.",
        components: [
          { name: "Content development", percent: "40%", note: "Video, interactive modules" },
          { name: "Platform costs", percent: "30%", note: "Mobile app, LMS" },
          { name: "Curation", percent: "20%", note: "Content selection, sequencing" },
          { name: "Analytics", percent: "10%", note: "Progress tracking" }
        ],
        roi_note: "Best for reinforcement; combine with higher-touch methods for skill building"
      },
      self_paced_elearning: {
        multiplier: 0.3,
        explanation: "0.3x multiplier is the lowest cost option. Scales infinitely with one-time development cost. No instructor, venue, or scheduling costs.",
        components: [
          { name: "Content development", percent: "50%", note: "One-time investment" },
          { name: "LMS platform", percent: "25%", note: "Hosting, delivery" },
          { name: "Maintenance", percent: "15%", note: "Updates, fixes" },
          { name: "Support", percent: "10%", note: "Help desk, FAQs" }
        ],
        roi_note: "Best ROI at scale (1000+ learners); beware low completion rates"
      }
    };
    
    return costData[modality.id] || {
      multiplier: modality.cost_multiplier,
      explanation: `${modality.cost_multiplier}x multiplier based on industry benchmarks for this modality type.`,
      components: [
        { name: "Development", percent: "35%", note: "Content creation" },
        { name: "Delivery", percent: "35%", note: "Facilitation/platform" },
        { name: "Materials", percent: "15%", note: "Resources" },
        { name: "Administration", percent: "15%", note: "Management" }
      ],
      roi_note: "Costs benchmarked against in-person ILT (1.0x baseline)"
    };
  };
  
  const effectivenessResearch = getEffectivenessResearch();
  const costResearch = getCostResearch();
  
  return (
    <div
      data-testid={`modality-card-${modality.id}`}
      onClick={onClick}
      className={`rounded-xl border-2 cursor-pointer transition-all duration-200 hover:shadow-md overflow-hidden
        ${selected ? 'border-blue-600 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}
    >
      {/* Main Card Content */}
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg ${selected ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
            <IconComponent size={20} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h4 className={`font-medium ${selected ? 'text-blue-900' : 'text-gray-900'}`}>
                {modality.name}
              </h4>
              {isBestValue && <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Best Value</span>}
              {isHighValue && !isBestValue && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">High Value</span>}
            </div>
            <p className="text-xs text-gray-500 mt-1">{modality.description}</p>
            
            <div className="grid grid-cols-2 gap-2 mt-3">
              {/* Effectiveness with info button */}
              <div>
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-500">Effectiveness</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowEffectivenessInfo(!showEffectivenessInfo); setShowCostInfo(false); }}
                    className="p-0.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600"
                    title="Why this effectiveness?"
                  >
                    <HelpCircle size={12} />
                  </button>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-emerald-500 rounded-full"
                      style={{ width: `${modality.effectiveness * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-semibold text-emerald-600">
                    {(modality.effectiveness * 100).toFixed(0)}%
                  </span>
                </div>
              </div>
              
              {/* Cost Multiplier with info button */}
              <div>
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-500">Cost Multiplier</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowCostInfo(!showCostInfo); setShowEffectivenessInfo(false); }}
                    className="p-0.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600"
                    title="Why this cost?"
                  >
                    <Info size={12} />
                  </button>
                </div>
                <span className={`block text-sm font-semibold ${
                  modality.cost_multiplier <= 0.5 ? 'text-emerald-600' :
                  modality.cost_multiplier <= 1.0 ? 'text-yellow-600' : 'text-rose-600'
                }`}>
                  {modality.cost_multiplier}x
                </span>
              </div>
            </div>
            
            {/* Best For Tags */}
            {modality.best_for && (
              <div className="flex flex-wrap gap-1 mt-2">
                {modality.best_for.slice(0, 3).map((item, i) => (
                  <span key={`bestfor-${item.slice(0, 20)}-${i}`} className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">
                    {item}
                  </span>
                ))}
              </div>
            )}
          </div>
          {selected && <CheckCircle2 className="text-blue-600 flex-shrink-0" size={20} />}
        </div>
      </div>
      
      {/* Effectiveness Info Panel */}
      {showEffectivenessInfo && (
        <div className="px-4 pb-4" onClick={(e) => e.stopPropagation()}>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            {/* Title row with icon inline */}
            <div className="flex items-center gap-2 mb-3">
              <div className="p-1.5 bg-emerald-100 rounded-lg">
                <TrendingUp className="text-emerald-600" size={16} />
              </div>
              <h4 className="font-semibold text-emerald-900 text-sm">
                {(modality.effectiveness * 100).toFixed(0)}% Learning Effectiveness
              </h4>
            </div>
            
            {/* Full-width content below */}
            <div className="space-y-3">
              <p className="text-xs text-emerald-700 font-medium">
                Based on: {effectivenessResearch.basis}
              </p>
              <p className="text-sm text-emerald-800 leading-relaxed">{effectivenessResearch.explanation}</p>
              
              {/* Effectiveness Drivers */}
              <div>
                <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-1.5">What Drives This Score</p>
                <div className="flex flex-wrap gap-1.5">
                  {effectivenessResearch.effectiveness_drivers.map((driver, i) => (
                    <span key={`driver-${driver.slice(0, 20)}-${i}`} className="text-xs px-2 py-1 bg-white/70 text-emerald-700 rounded-full border border-emerald-200">
                      {driver}
                    </span>
                  ))}
                </div>
              </div>
              
              {/* Research Studies */}
              <div>
                <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-1.5">Supporting Research</p>
                <ul className="space-y-1">
                  {effectivenessResearch.studies.map((study, i) => (
                    <li key={study.name || `study-${i}`} className="text-xs text-gray-600 flex items-start gap-1.5">
                      <CheckCircle size={10} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span>{study}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Cost Info Panel */}
      {showCostInfo && (
        <div className="px-4 pb-4" onClick={(e) => e.stopPropagation()}>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            {/* Title row with icon inline */}
            <div className="flex items-center gap-2 mb-3">
              <div className="p-1.5 bg-amber-100 rounded-lg">
                <DollarSign className="text-amber-600" size={16} />
              </div>
              <h4 className="font-semibold text-amber-900 text-sm">
                {costResearch.multiplier}x Cost Multiplier
              </h4>
            </div>
            
            {/* Full-width content below */}
            <div className="space-y-3">
              <p className="text-sm text-amber-800 leading-relaxed">{costResearch.explanation}</p>
              
              {/* Cost Components */}
              <div>
                <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2">Cost Breakdown</p>
                <div className="space-y-2">
                  {costResearch.components.map((comp, i) => (
                    <div key={comp.label || `cost-${i}`} className="flex items-center gap-2">
                      <div className="w-12 text-right">
                        <span className="text-xs font-semibold text-amber-700">{comp.percent}</span>
                      </div>
                      <div className="w-24 h-2 bg-amber-200 rounded-full overflow-hidden flex-shrink-0">
                        <div 
                          className="h-full bg-amber-500 rounded-full"
                          style={{ width: comp.percent }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-xs text-gray-700 font-medium">{comp.name}</span>
                        <span className="text-xs text-gray-500 ml-1">({comp.note})</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* ROI Note */}
              <div className="p-2.5 bg-white/70 rounded-lg border border-amber-200">
                <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-1">ROI Insight</p>
                <p className="text-sm text-gray-700 leading-relaxed">{costResearch.roi_note}</p>
              </div>
              
              {/* Source */}
              <p className="text-xs text-gray-500 italic">
                Source: ATD State of Industry 2024, Training Industry Benchmarks, Brandon Hall Research
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
